'use client';

import { ArrowLeft, Info } from 'lucide-react';
import { useState } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import type { Bot } from '@/types/bot';
import { getBotAvatarSrc } from './getBotAvatarSrc';

interface BotChatHeaderProps {
  bot: Bot;
  onBack: () => void;
  remainingMessages?: number | null;
  className?: string;
}

export function BotChatHeader({
  bot,
  onBack,
  remainingMessages,
  className,
}: BotChatHeaderProps) {
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const persona = bot.persona;
  const avatarSrc = getBotAvatarSrc(bot);

  return (
    <header
      className={cn('jr-subheader shrink-0', className)}
      data-testid="bot-chat-header"
    >
      <div className="flex w-full min-w-0 items-center gap-3">
        {/* Back button */}
        <Button
          variant="ghost"
          size="icon"
          onClick={onBack}
          className="shrink-0 rounded-full text-[var(--jr-text)] hover:bg-[color:rgba(255,255,255,0.06)]"
          data-testid="bot-chat-back-button"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>

        <div className="flex-1 min-w-0">
          <div className="flex min-w-0 items-center gap-2">
            <h1 className="jr-display flex-1 min-w-0 truncate text-[1.9rem] font-semibold leading-none">{bot.name}</h1>
            <Badge variant="secondary" className="jr-pill shrink-0 border-[color:rgba(214,168,139,0.18)] text-xs">
              AI
            </Badge>
          </div>
          <p className="truncate text-sm text-[var(--jr-text-soft)]">
            {persona.description}
          </p>
        </div>

        {/* Remaining messages indicator */}
        {remainingMessages !== null && remainingMessages !== undefined && (
          <div className="shrink-0 text-right text-xs text-[var(--jr-text-muted)]">
            <span className="font-medium text-[var(--jr-accent)]">{remainingMessages}</span>
            <span className="block">msgs left</span>
          </div>
        )}

        {/* Info sheet trigger */}
        <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
          <SheetTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="shrink-0 rounded-full text-[var(--jr-text)] hover:bg-[color:rgba(255,255,255,0.06)]"
              data-testid="bot-info-button"
            >
              <Info className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <SheetContent>
            <SheetHeader>
              <div className="flex items-center gap-3 mb-2">
                <Avatar className="jr-avatar-ring h-14 w-14">
                  <AvatarImage src={avatarSrc} alt={bot.name} />
                  <AvatarFallback className="bg-[color:var(--jr-surface-muted)] text-xl font-semibold text-[color:var(--jr-accent)]">
                    {bot.name.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <SheetTitle className="jr-display text-left text-3xl">{bot.name}</SheetTitle>
                  <Badge variant="secondary" className="jr-pill mt-1 text-xs">
                    AI Companion
                  </Badge>
                </div>
              </div>
              <SheetDescription className="text-left text-[var(--jr-text-soft)]">
                {persona.description}
              </SheetDescription>
            </SheetHeader>

            <div className="mt-6 space-y-6">
              {/* Personality */}
              <div>
                <h3 className="text-sm font-semibold mb-2">Personality</h3>
                <div className="flex flex-wrap gap-2">
                  {persona.personality_traits.map((trait) => (
                    <Badge key={trait} variant="outline" className="jr-pill border-[color:rgba(214,168,139,0.16)] bg-transparent text-xs text-[var(--jr-text-soft)]">
                      {trait}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Tone */}
              <div>
                <h3 className="text-sm font-semibold mb-2">Tone</h3>
                <p className="text-sm capitalize text-[var(--jr-text-soft)]">{persona.tone}</p>
              </div>

              {/* Knowledge Areas */}
              <div>
                <h3 className="text-sm font-semibold mb-2">Expertise</h3>
                <div className="flex flex-wrap gap-2">
                  {persona.knowledge_base.map((knowledge) => (
                    <Badge key={knowledge} variant="secondary" className="jr-pill text-xs">
                      {knowledge}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Conversation Starters */}
              <div>
                <h3 className="text-sm font-semibold mb-2">Try asking</h3>
                <ul className="space-y-2">
                  {persona.greeting_messages.slice(0, 3).map((greeting, index) => (
                    <li
                      key={index}
                      className="jr-card-muted rounded-2xl px-3 py-2 text-sm text-[var(--jr-text-soft)]"
                    >
                      "{greeting}"
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
}
